import apiClient from './apiClient';

/**
 * Upload Service for backend DICOM upload workflow
 * Uses three-step process: initiate -> upload to S3 -> complete
 */

const uploadService = {
  /**
   * Step 1: Initiate upload - Get presigned S3 URLs
   * @param {Array<File>} files - Array of DICOM File objects
   * @returns {Promise<{study_id: string, upload_urls: Array, storage_path: string}>}
   */
  initiateUpload: async (files) => {
    try {
      const fileMetadata = files.map(file => ({
        filename: file.name,
        size: file.size,
        content_type: 'application/dicom',
      }));

      const response = await apiClient.post('/studies/upload/initiate', {
        files: fileMetadata,
      });

      return response.data;
    } catch (error) {
      // Handle specific error cases
      if (error.response?.status === 403) {
        if (error.response.data?.detail?.includes('quota')) {
          throw new Error('Storage quota exceeded. Please contact your administrator.');
        } else if (error.response.data?.detail?.includes('permission')) {
          throw new Error('You do not have permission to upload studies.');
        }
      }
      
      throw new Error(error.response?.data?.detail || 'Failed to initiate upload. Please try again.');
    }
  },

  /**
   * Step 2: Upload file to S3 using presigned POST
   * Uses XMLHttpRequest for progress tracking with FormData
   * @param {File} file - File object to upload
   * @param {string} uploadUrl - Presigned S3 URL
   * @param {Object} uploadFields - Presigned POST fields (key, policy, signature, etc.)
   * @param {Function} onProgress - Callback for progress updates (0-100)
   * @returns {Promise<void>}
   */
  uploadFileToPresignedUrl: async (file, uploadUrl, uploadFields, onProgress) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100;
          if (onProgress) {
            onProgress(percentComplete);
          }
        }
      });

      // Handle successful upload
      xhr.addEventListener('load', () => {
        // S3 presigned POST returns 204 No Content on success
        if (xhr.status === 204 || xhr.status === 200) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      // Handle network errors
      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      // Handle upload abort
      xhr.addEventListener('abort', () => {
        reject(new Error('Upload was aborted'));
      });

      // Build FormData with presigned fields + file
      const formData = new FormData();
      
      // Add all presigned fields FIRST
      Object.entries(uploadFields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      
      // Add file LAST (important!)
      formData.append('file', file);

      // Open connection and send - use POST, not PUT
      xhr.open('POST', uploadUrl);
      // Don't set Content-Type - let browser set it for multipart/form-data
      xhr.send(formData);
    });
  },

  /**
   * Step 3: Complete upload - Trigger backend processing
   * @param {string} studyId - Study ID from initiate response
   * @returns {Promise<{message: string, study_id: string, task_id: string, status: string}>}
   */
  completeUpload: async (studyId) => {
    try {
      const response = await apiClient.post('/studies/upload/complete', {
        study_id: studyId,
      });

      return response.data;
    } catch (error) {
      // Handle 409 Conflict - Duplicate study
      if (error.response?.status === 409) {
        const errorData = error.response.data;
        
        if (errorData.status === 'duplicate') {
          // Create a custom error object with duplicate info
          const duplicateError = new Error('Duplicate study detected');
          duplicateError.isDuplicate = true;
          duplicateError.duplicateData = errorData;
          throw duplicateError;
        }
      }
      
      throw new Error(error.response?.data?.detail || 'Failed to complete upload. Please try again.');
    }
  },

  /**
   * Complete workflow: Upload all files for a study
   * @param {Array<File>} files - Array of DICOM files
   * @param {Function} onProgress - Callback for overall progress (receives: currentFile, totalFiles, percentComplete)
   * @returns {Promise<{study_id: string, task_id: string}>}
   */
  uploadStudy: async (files, onProgress) => {
    try {
      // Step 1: Initiate upload
      const { study_id, upload_urls } = await uploadService.initiateUpload(files);

      // Step 2: Upload each file to S3
      for (let i = 0; i < upload_urls.length; i++) {
        const urlInfo = upload_urls[i];
        const file = files.find(f => f.name === urlInfo.filename);

        if (!file) {
          throw new Error(`File not found: ${urlInfo.filename}`);
        }

        // Upload with progress tracking using presigned POST
        await uploadService.uploadFileToPresignedUrl(
          file,
          urlInfo.upload_url,
          urlInfo.upload_fields,
          (fileProgress) => {
            if (onProgress) {
              onProgress(i + 1, upload_urls.length, fileProgress);
            }
          }
        );
      }

      // Step 3: Complete upload
      const result = await uploadService.completeUpload(study_id);

      return {
        study_id: result.study_id,
        task_id: result.task_id,
      };
    } catch (error) {
      throw error;
    }
  },
};

export default uploadService;